import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import type { CreatePaymentResponse, Order, PaymentProviderName, Product } from "@cross-border-agent/contracts";
import { api } from "./api";
import { PaymentMethods, preferredPaymentProvider } from "./CommerceCatalog";

export function Checkout({ product, providers, initialOrder, onOrderCreated, onClose, onPaid }: {
  product: Product; providers: PaymentProviderName[]; initialOrder?: Order;
  onOrderCreated?: (order: Order) => void; onClose: () => void; onPaid: (order: Order) => void;
}) {
  const [provider, setProvider] = useState(() => preferredPaymentProvider(providers));
  const [order, setOrder] = useState<Order | null>(initialOrder || null);
  const [payment, setPayment] = useState<CreatePaymentResponse | null>(null);
  const [qr, setQr] = useState(""); const [message, setMessage] = useState(""); const [busy, setBusy] = useState(false);
  const alive = useRef(true), paying = useRef(false), notified = useRef(false);
  const callbacks = useRef({ onPaid, onOrderCreated, onClose }); callbacks.current = { onPaid, onOrderCreated, onClose };
  const dialog = useRef<HTMLElement>(null);
  const accept = (latest: Order) => {
    if (!alive.current) return;
    setOrder(latest);
    if (latest.status === "fulfilled" && !notified.current) { notified.current = true; callbacks.current.onPaid(latest); }
  };
  useEffect(() => {
    alive.current = true; const previous = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => { alive.current = false; previous?.focus(); };
  }, []);
  useEffect(() => {
    let current = true;
    if (payment?.action.type === "qr_code") void QRCode.toDataURL(payment.action.content, { width: 220, margin: 1 }).then(value => { if (current) setQr(value); });
    return () => { current = false; };
  }, [payment]);
  useEffect(() => {
    if (!order || order.status !== "pending_payment") return;
    let running = false, cancelled = false;
    const timer = setInterval(async () => {
      if (running) return; running = true;
      try { const latest = await api.getOrder(order.orderNo); if (!cancelled) accept(latest); }
      catch { if (!cancelled && alive.current) setMessage("暂时无法核对付款结果，请勿重复购买，可稍后从订单继续。"); }
      finally { running = false; }
    }, 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [order?.orderNo, order?.status]);
  const pay = async () => {
    if (!provider || !providers.includes(provider) || paying.current || payment) return;
    paying.current = true; setBusy(true); setMessage("");
    try {
      const nextOrder = order || await api.createOrder(product.code);
      if (!alive.current) return;
      setOrder(nextOrder); callbacks.current.onOrderCreated?.(nextOrder);
      if (nextOrder.status === "fulfilled") { accept(nextOrder); return; }
      const nextPayment = await api.createPayment(nextOrder.orderNo, provider);
      if (!alive.current) return;
      setPayment(nextPayment);
      if (nextPayment.action.type === "redirect") location.href = nextPayment.action.url;
      if (nextPayment.action.type === "html_form") {
        const host = document.createElement("div"); host.innerHTML = nextPayment.action.html;
        document.body.appendChild(host); (host.querySelector("form") as HTMLFormElement | null)?.submit();
      }
    } catch (error) { if (alive.current) setMessage(error instanceof Error ? error.message : "支付创建失败"); }
    finally { paying.current = false; if (alive.current) setBusy(false); }
  };
  const mock = async () => {
    if (!order || paying.current) return; paying.current = true; setBusy(true);
    try { accept(await api.mockSucceed(order.orderNo)); }
    catch (error) { if (alive.current) setMessage(error instanceof Error ? error.message : "模拟支付失败"); }
    finally { paying.current = false; if (alive.current) setBusy(false); }
  };
  return <div className="modal-backdrop"><section ref={dialog} className="checkout" role="dialog" aria-modal="true" aria-labelledby="checkout-title" onKeyDown={event => {
    if (event.key === "Escape" && !busy) onClose();
    if (event.key === "Tab") {
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href]')];
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }}>
    <header><div><span>确认订单</span><h2 id="checkout-title">{product.name}</h2></div><button className="close" disabled={busy} onClick={onClose}>关闭</button></header>
    <div className="checkout-price"><span>应付金额</span><strong>¥{((order?.amountCents ?? product.priceCents) / 100).toFixed(2)}</strong></div>
    <p>{product.description}</p>{order && <small>订单号：{order.orderNo}（可从订单记录继续）</small>}
    <PaymentMethods providers={providers} selected={provider} onSelect={setProvider} locked={busy || Boolean(payment)} />
    {qr && <div className="qr"><img src={qr} alt="微信支付二维码" /><span>请使用微信扫码支付</span></div>}
    {payment?.action.type === "mock" && <button className="mock" disabled={busy} onClick={() => void mock()}>确认本地模拟支付成功</button>}
    {message && <p className="form-message" role="alert">{message}</p>}
    <button className="primary wide" disabled={busy || Boolean(payment) || !provider || !providers.includes(provider) || Boolean(order && order.status !== "pending_payment")} onClick={() => void pay()}>{busy ? "正在创建" : payment ? "等待付款" : provider === "mock" ? "开始本地模拟支付" : "确认支付"}</button>
    <small>只有服务端确认渠道支付成功后，套餐、积分或真人服务才会生效。</small>
  </section></div>;
}
